import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapPin, Loader2, Search, Filter } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Contact } from '../types/Contact';
import { useGroupContext } from '../lib/groups/context';
// Core imports
import { listCollectionsCore, listMembershipsForCollectionsCore } from '@gv/core';
import { listContactsCore, listContactsForGroupCore, updateContactCoordinatesCore } from '@gv/core';

interface ContactMapProps {
    user: any;
    onContactSelect: (contact: Contact) => void;
    isVisible?: boolean;
}

interface ContactWithLocation extends Contact {
    latitude?: number;
    longitude?: number;
    locationString?: string;
}

interface MapLocation {
    id: string;
    latitude: number;
    longitude: number;
    contacts: ContactWithLocation[];
    locationString: string;
}

// Declare Google Maps types
declare global {
    interface Window {
        google: typeof google;
        initMap: () => void;
        selectContact: (contactId: string) => void;
    }
}

export default function GoogleContactMap({ user, onContactSelect, isVisible = true }: ContactMapProps) {
    const [contacts, setContacts] = useState<ContactWithLocation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [mapLoading, setMapLoading] = useState(true);
    const [geocodingProgress, setGeocodingProgress] = useState({ current: 0, total: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredContacts, setFilteredContacts] = useState<ContactWithLocation[]>([]);
    type FieldFilter = 'name' | 'company' | 'location' | 'collection' | 'status' | 'relationship' | 'updated';
    const allFieldFilters: FieldFilter[] = ['name', 'company', 'location', 'collection', 'status', 'relationship', 'updated'];
    const [selectedFieldFilters, setSelectedFieldFilters] = useState<FieldFilter[]>(allFieldFilters);
    const [showFieldDropdown, setShowFieldDropdown] = useState(false);
    const filterDropdownRef = useRef<HTMLDivElement | null>(null);
    const filterButtonRef = useRef<HTMLButtonElement | null>(null);
    // Collections for omnibar parity with ContactList
    const [collections, setCollections] = useState<{ id: string; name: string; color?: string }[]>([]);
    const [contactCollectionMap, setContactCollectionMap] = useState<Map<string, string[]>>(new Map());

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const currentInfoWindowRef = useRef<any>(null);
    const geocodingInitiated = useRef<boolean>(false);
    const [apiKey, setApiKey] = useState<string | null>(null);
    const { activeGroupId } = useGroupContext();
    const [mapId, setMapId] = useState<string | null>(null);
    const [mapIdLight, setMapIdLight] = useState<string | null>(null);
    const [mapIdDark, setMapIdDark] = useState<string | null>(null);
    // Lock to dark mode for the Google Map; UI stays dark as well
    const computedTheme: 'light' | 'dark' = 'dark';
    // Base layer toggle: 'road' (roadmap) vs 'real' (hybrid)
    const [baseLayer, setBaseLayer] = useState<'road' | 'real'>(() => {
        try {
            const saved = localStorage.getItem('mapBaseLayer');
            return saved === 'real' ? 'real' : 'road';
        } catch {
            return 'road';
        }
    });
    const theme = useMemo(() => ({
        appBg: 'bg-gray-900',
        appText: 'text-gray-100',
        panelBg: 'bg-gray-900',
        panelBorder: 'border-gray-700',
        inputBg: 'bg-gray-800',
        inputBorder: 'border-gray-600',
        inputText: 'text-gray-100',
        inputPlaceholder: 'placeholder-gray-400',
        // Blue-tinted, on-brand bottom bar styling
        barBg: 'bg-gradient-to-r from-blue-950/90 via-black/95 to-blue-950/90',
        barBorder: 'border-gray-700',
        overlayBg: 'bg-gray-900/95',
        overlayText: '#e5e7eb',
        overlaySub: '#9ca3af',
        chipBg: 'bg-gray-800',
        chipText: 'text-gray-200',
    }), []);
    // Theme toggle disabled: locked to dark mode with faces
    // Persist map center/zoom across back navigation
    const restoredViewRef = useRef<boolean>(false);

    // Prevent body scrolling when map is mounted AND visible
    useEffect(() => {
        if (isVisible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isVisible]);

    // Build dynamic location labels from contacts (not used for parsing now, but could power future suggestions)
    const locationOptions = useMemo(() => {
        const set = new Set<string>();
        contacts.forEach(c => { if (c.locationString) set.add(c.locationString); });
        return Array.from(set).sort().map(v => ({ value: v, label: v }));
    }, [contacts]);

    // Keep filter dropdown open until the user toggles the filter button again

    // Fetch collections and mapping for omnibar
    useEffect(() => {
        const fetchCollections = async () => {
            if (!user?.id) return;
            try {
                const cols = await listCollectionsCore(supabase as any, user.id);
                setCollections(cols || []);
                const ids = (cols || []).map(c => c.id);
                if (ids.length) {
                    const members = await listMembershipsForCollectionsCore(supabase as any, ids);
                    const mapping = new Map<string, string[]>();
                    (members || []).forEach((m: any) => {
                        const ex = mapping.get(m.contact_id) || [];
                        mapping.set(m.contact_id, [...ex, m.collection_id]);
                    });
                    setContactCollectionMap(mapping);
                } else {
                    setContactCollectionMap(new Map());
                }
            } catch (e) {
                console.error('Collections fetch failed (map):', e);
            }
        };
        fetchCollections();
    }, [user?.id]);

    // Simple, user-selected field filters (OR matching across selected fields)
    const parsedUpdatedDays = useMemo(() => {
        const q = (searchQuery || '').toLowerCase();
        const m = q.match(/(\d+)d/);
        if (!m) return undefined;
        const n = Number(m[1]);
        return isNaN(n) ? undefined : n;
    }, [searchQuery]);

    // Helper for name token matching
    const matchesNameTokens = (contact: Contact, input: string) => {
        const s = input.trim().toLowerCase().replace(/\s+/g, ' ');
        if (!s) return true;
        const tokens = s.split(' ').filter(Boolean);
        const fn = (contact.first_name || '').toLowerCase();
        const ln = (contact.last_name || '').toLowerCase();
        const nn = (contact.nickname || '').toLowerCase();
        const pre = (contact.name_prefix || '').toLowerCase();
        const suf = (contact.name_suffix || '').toLowerCase();
        const full = `${fn} ${ln}`.trim().replace(/\s+/g, ' ');
        const rev = `${ln} ${fn}`.trim().replace(/\s+/g, ' ');
        const fields = [fn, ln, nn, pre, suf, full, rev];
        return tokens.every(t => fields.some(f => f.includes(t)));
    };

    const contactMatchesSelectedFields = (c: ContactWithLocation) => {
        const q = (searchQuery || '').trim().toLowerCase();
        // When query is empty, show all contacts by default
        if (!q) return true;
        const checks: Record<FieldFilter, boolean> = {
            name: matchesNameTokens(c, q),
            company: ((c.company || c.company_name || c.business_name || '').toLowerCase().includes(q)),
            location: (() => {
                const locs = [
                    c.locationString,
                    c.city && c.state ? `${c.city}, ${c.state}` : undefined,
                    c.address_1_city && c.address_1_region ? `${c.address_1_city}, ${c.address_1_region}` : undefined,
                    c.business_city && c.business_state ? `${c.business_city}, ${c.business_state}` : undefined,
                    c.city,
                    c.address_1_city,
                    c.business_city,
                    c.state,
                    c.address_1_region,
                    c.business_state,
                    c.location_info,
                ].filter(Boolean) as string[];
                return locs.some(ls => (ls || '').toLowerCase().includes(q));
            })(),
            collection: (() => {
                if (!collections.length) return false;
                const hitIds = new Set<string>(collections.filter(col => col.name.toLowerCase().includes(q)).map(c => c.id));
                if (hitIds.size === 0) return false;
                const ccols = contactCollectionMap.get(c.id || '') || [];
                return ccols.some(id => hitIds.has(id));
            })(),
            status: (c.status || '').toLowerCase().includes(q),
            relationship: (c.relationship_type || '').toLowerCase().includes(q),
            updated: (() => {
                if (!parsedUpdatedDays || parsedUpdatedDays <= 0) return false;
                const threshold = Date.now() - parsedUpdatedDays * 24 * 60 * 60 * 1000;
                const ts = new Date(c.updated_at || c.created_at || '').getTime();
                return !isNaN(ts) && ts >= threshold;
            })(),
        };
        return selectedFieldFilters.some(f => checks[f]);
    };

    // Compute filtered contacts with simple field filters
    useEffect(() => {
        const filtered = contacts.filter(contactMatchesSelectedFields);
        setFilteredContacts(filtered);
    }, [contacts, searchQuery, selectedFieldFilters, contactCollectionMap, collections, parsedUpdatedDays]);

    // Filter markers when filtered contacts change
    useEffect(() => {
        if (markersRef.current.length > 0) {
            updateMarkerVisibility();
        }
    }, [filteredContacts]);

    const updateMarkerVisibility = () => {
        const map = mapInstanceRef.current;
        markersRef.current.forEach((markerData) => {
            const shouldShow = markerData.groupContacts.some((c: ContactWithLocation) => contactMatchesSelectedFields(c));
            const m = markerData.marker;
            if (!m) return;
            // Classic Marker API
            if (typeof m.setVisible === 'function') {
                m.setVisible(shouldShow);
                // AdvancedMarkerElement API
            } else if ('map' in m) {
                m.map = shouldShow ? map : null;
                // Fallback: try DOM visibility
            } else if ('content' in m && (m as any).content instanceof HTMLElement) {
                (m as any).content.style.display = shouldShow ? '' : 'none';
            }
        });
    };

    const focusContactOnMap = (id: string) => {
        const markerData = markersRef.current.find(m => m.contact.id === id);
        const contact = contacts.find(c => c.id === id);
        if (markerData && mapInstanceRef.current) {
            const lat = parseFloat(String(markerData.contact.latitude));
            const lng = parseFloat(String(markerData.contact.longitude));
            if (!isNaN(lat) && !isNaN(lng)) {
                mapInstanceRef.current.setCenter({ lat, lng });
                mapInstanceRef.current.setZoom(12);
                // Emulate click to open info window
                window.setTimeout(() => {
                    try {
                        // Try both event types for compatibility
                        (window as any).google.maps.event.trigger(markerData.marker, 'click');
                        (window as any).google.maps.event.trigger(markerData.marker, 'gmp-click');
                    } catch { }
                }, 150);
            }
        } else if (contact && mapInstanceRef.current) {
            // If we have contact but not marker (no coords yet), attempt geocoding string centering later
            const loc = contact.locationString;
            if (loc) {
                try {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: loc }, (results: any, status: any) => {
                        if (status === 'OK' && results[0]) {
                            const p = results[0].geometry.location;
                            mapInstanceRef.current.setCenter(p);
                            mapInstanceRef.current.setZoom(12);
                        }
                    });
                } catch { /* ignore */ }
            }
        }
    };

    // Removed tier filters; omnibar handles all filters now

    // Fetch Google Maps API key from Supabase secrets
    useEffect(() => {
        const fetchApiKey = async () => {
            try {
                console.log('Fetching Google Maps API key...');
                const { data, error } = await supabase.functions.invoke('get-google-maps-key');
                if (error) {
                    console.error('Supabase function error:', error);
                    throw error;
                }
                if (!data || !data.apiKey) {
                    throw new Error('No API key returned from function');
                }
                console.log('API key retrieved successfully');
                setApiKey(data.apiKey);
                if (data.mapId) setMapId(data.mapId);
                if (data.mapIdLight) setMapIdLight(data.mapIdLight);
                if (data.mapIdDark) setMapIdDark(data.mapIdDark);
            } catch (error) {
                console.error('Error fetching Google Maps API key:', error);
                setError('Failed to load Google Maps API key. Please check your configuration.');
                setLoading(false);
                setMapLoading(false);
            }
        };

        if (isVisible) {
            fetchApiKey();
        }
    }, [isVisible]);

    // Load Google Maps script when apiKey is available; include Advanced Marker library only if a Map ID is present
    useEffect(() => {
        if (!apiKey || !isVisible) return;
        if (window.google && window.google.maps) {
            setMapLoading(false);
            return;
        }
        const existing = document.getElementById('google-maps-script') as HTMLScriptElement | null;
        if (existing) return;
        (window as any).initMap = () => {
            setMapLoading(false);
        };
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        const hasAnyMapId = Boolean(mapId || mapIdLight || mapIdDark);
        const libsPart = hasAnyMapId ? '&libraries=marker' : '';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap${libsPart}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
            setError('Failed to load Google Maps script.');
            setMapLoading(false);
        };
        document.body.appendChild(script);
        return () => {
            // do not remove script to avoid reloading on tab toggles
        };
    }, [apiKey, isVisible, mapId, mapIdLight, mapIdDark]);

    // Fetch contacts scoped to the active vault (group) when available; otherwise fall back to the user's own contacts
    useEffect(() => {
        const fetchContacts = async () => {
            if (!user?.id) return;
            try {
                setLoading(true);
                const list = (activeGroupId
                    ? await listContactsForGroupCore(supabase as any, activeGroupId, { includeArchived: false, limit: 10000 })
                    : await listContactsCore(supabase as any, { includeArchived: false, limit: 10000 })
                ) as any as Contact[];
                if (window.google && window.google.maps) {
                    await geocodeContacts(list);
                } else {
                    // Wait a tick for maps to load, then geocode
                    const id = window.setInterval(async () => {
                        if (window.google && window.google.maps) {
                            window.clearInterval(id);
                            await geocodeContacts(list);
                        }
                    }, 200);
                }
            } catch (e) {
                console.error('Failed to fetch contacts for map:', e);
                setError('Failed to load contacts for map.');
                setLoading(false);
            }
        };
        if (apiKey && !mapLoading && isVisible) {
            fetchContacts();
        }
    }, [apiKey, mapLoading, isVisible, user?.id, activeGroupId]);

    // Initialize map after contacts and Google Maps are ready
    useEffect(() => {
        if (!mapLoading && contacts.length > 0 && mapRef.current && (window.google && window.google.maps)) {
            initializeMap();
        }
    }, [mapLoading, contacts]);

    const getContactLocationString = (contact: Contact): string | null => {
        // Try different location fields in order of preference
        const locationSources = [
            contact.address_1_formatted,
            contact.address_1_city && contact.address_1_region && `${contact.address_1_city}, ${contact.address_1_region}`,
            contact.address_1_city && contact.address_1_country && `${contact.address_1_city}, ${contact.address_1_country}`,
            contact.address_2_formatted,
            contact.address_2_city && contact.address_2_region && `${contact.address_2_city}, ${contact.address_2_region}`,
            contact.address_2_city && contact.address_2_country && `${contact.address_2_city}, ${contact.address_2_country}`,
            contact.city && contact.state && `${contact.city}, ${contact.state}`,
            contact.city && contact.country && `${contact.city}, ${contact.country}`,
            contact.address,
            contact.location_info,
            contact.business_city && contact.business_state && `${contact.business_city}, ${contact.business_state}`,
            contact.business_city && contact.business_country && `${contact.business_city}, ${contact.business_country}`,
        ].filter(Boolean);

        for (const location of locationSources) {
            if (location && typeof location === 'string' && location.trim()) {
                const cleanLocation = location.trim();

                // Validate that it's not just a country code or single letters and has some structure
                if (
                    cleanLocation.length > 2 &&
                    !cleanLocation.match(/^[A-Z]{1,3}$/) &&
                    (cleanLocation.includes(' ') || cleanLocation.includes(','))
                ) {
                    return cleanLocation;
                }
            }
        }

        return null;
    };

    const geocodeContacts = async (contactList: Contact[]) => {
        if (!window.google) return;

        const geocoder = new window.google.maps.Geocoder();
        const geocodedContacts: ContactWithLocation[] = [];

        // First, load any existing coordinates from the database, scoped to the active vault when present
        // We already fetched full contacts as input; use those that already have coords
        const existingContacts = contactList.filter((c: any) => c.latitude != null && c.longitude != null).map((c: any) => ({ id: c.id, latitude: c.latitude, longitude: c.longitude }));

        // Create a map of existing coordinates
        const existingCoords = new Map();
        (existingContacts || []).forEach(contact => {
            existingCoords.set(contact.id, {
                latitude: contact.latitude,
                longitude: contact.longitude
            });
        });

        // Filter contacts that need geocoding
        const contactsNeedingGeocoding = contactList.filter(contact => {
            const locationString = getContactLocationString(contact);
            return locationString && !existingCoords.has(contact.id);
        });

        console.log(`Total contacts with locations: ${contactList.length}`);
        console.log(`Contacts with existing coordinates: ${existingCoords.size}`);
        console.log(`Contacts needing geocoding: ${contactsNeedingGeocoding.length}`);

        setGeocodingProgress({ current: 0, total: contactsNeedingGeocoding.length });

        // If no contacts need geocoding, just load existing ones
        if (contactsNeedingGeocoding.length === 0) {
            console.log('All contacts already have coordinates, loading existing data...');
            for (const contact of contactList) {
                const locationString = getContactLocationString(contact);
                if (!locationString) continue;

                if (existingCoords.has(contact.id)) {
                    const coords = existingCoords.get(contact.id);
                    geocodedContacts.push({
                        ...contact,
                        latitude: coords.latitude,
                        longitude: coords.longitude,
                        locationString
                    });
                }
            }
            setContacts(geocodedContacts);
            setLoading(false);
            return;
        }

        // Process all contacts (with existing or new coordinates)
        for (const contact of contactList) {
            const locationString = getContactLocationString(contact);
            if (!locationString) continue;

            // Check if we already have coordinates
            if (existingCoords.has(contact.id)) {
                const coords = existingCoords.get(contact.id);
                geocodedContacts.push({
                    ...contact,
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    locationString
                });
                continue;
            }

            // Need to geocode this contact
            try {
                const result = await new Promise<any>((resolve, reject) => {
                    geocoder.geocode({ address: locationString }, (results: any, status: any) => {
                        if (status === 'OK' && results[0]) {
                            resolve(results[0]);
                        } else {
                            reject(new Error(`Geocoding failed: ${status}`));
                        }
                    });
                });

                const location = result.geometry.location;
                const latitude = location.lat();
                const longitude = location.lng();

                // Save coordinates to database
                try {
                    await updateContactCoordinatesCore(supabase as any, contact.id, latitude, longitude);
                } catch (saveError) {
                    console.error('Error saving coordinates for contact:', contact.id, saveError);
                }

                geocodedContacts.push({
                    ...contact,
                    latitude,
                    longitude,
                    locationString
                });

                setGeocodingProgress(prev => ({
                    ...prev,
                    current: prev.current + 1
                }));

                // Add delay to avoid hitting rate limits
                await new Promise(resolve => setTimeout(resolve, 100));

            } catch (error) {
                console.warn(`Failed to geocode ${locationString}:`, error);
                // Still add the contact without coordinates
                geocodedContacts.push({
                    ...contact,
                    locationString
                });
            }
        }

        setContacts(geocodedContacts);
        setLoading(false);
    };

    const createCustomMarker = (contact: ContactWithLocation, count: number = 1, allowAdvanced: boolean = false) => {
        // Prefer HTML-based markers if Google Marker library is available
        try {
            const hasAdv = (window as any).google?.maps?.marker?.AdvancedMarkerElement;
            const Pin = (window as any).google?.maps?.marker?.PinElement;
            // Only use AdvancedMarker when an active Map ID is set on the map
            if (allowAdvanced && hasAdv && Pin) {
                // Build glyph: circular image or initials
                const glyph = document.createElement('div');
                glyph.style.width = '28px';
                glyph.style.height = '28px';
                glyph.style.borderRadius = '50%';
                glyph.style.overflow = 'hidden';
                glyph.style.display = 'flex';
                glyph.style.alignItems = 'center';
                glyph.style.justifyContent = 'center';
                glyph.style.color = '#fff';
                glyph.style.fontWeight = '700';
                glyph.style.fontSize = '12px';
                const src = contact.profile_image_url ? (contact.profile_image_url.startsWith('http') ? contact.profile_image_url : `https://${contact.profile_image_url}`) : '';
                if (src) {
                    const img = document.createElement('img');
                    img.src = src;
                    img.alt = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                    img.style.width = '100%';
                    img.style.height = '100%';
                    img.style.objectFit = 'cover';
                    img.referrerPolicy = 'no-referrer';
                    img.crossOrigin = 'anonymous';
                    img.onerror = () => {
                        glyph.textContent = count > 1 ? String(count) : `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`;
                    };
                    glyph.appendChild(img);
                } else {
                    glyph.textContent = count > 1 ? String(count) : `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`;
                }

                const tcol = '#2563eb'; // unified brand color for pin body
                const pin = new Pin({
                    background: tcol,
                    borderColor: '#ffffff',
                    glyph,
                    scale: 1.1,
                });
                return new (window as any).google.maps.marker.AdvancedMarkerElement({ content: pin.element });
            }
        } catch { /* fall through to SVG */ }
        const tierColors = {
            'A': '#ef4444', // red
            'B': '#f97316', // orange  
            'C': '#eab308', // yellow
            'D': '#22c55e', // green
            'Unassigned': '#6b7280' // gray
        };

        const tierColor = tierColors[contact.tier as keyof typeof tierColors] || tierColors['Unassigned'];
        const pinHeight = 60;
        const pinWidth = 40;

        // Use a robust initials/count marker only (avoid embedding external images into SVG which often breaks due to CORS/security in data URLs)
        const displayText = count > 1 ? count.toString() : `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`;
        const fontSize = count > 1 ? '14' : '11';

        const svgMarker = `
            <svg width="${pinWidth}" height="${pinHeight}" viewBox="0 0 ${pinWidth} ${pinHeight}" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <filter id="drop-shadow-${contact.id}" x="-50%" y="-50%" width="200%" height="200%">
                        <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
                    </filter>
                </defs>
                <!-- Single teardrop body with shadow -->
                <path d="M${pinWidth / 2 - 18} 36 Q${pinWidth / 2 - 18} 50 ${pinWidth / 2} 58 Q${pinWidth / 2 + 18} 50 ${pinWidth / 2 + 18} 36 A18 18 0 0 0 ${pinWidth / 2 - 18} 36 Z" fill="#00000020"/>
                <path d="M${pinWidth / 2 - 16} 20 A16 16 0 1 0 ${pinWidth / 2 + 16} 20 Q${pinWidth / 2} 56 ${pinWidth / 2 - 16} 20 Z" 
                      fill="${tierColor}" stroke="#ffffff" stroke-width="2" filter="url(#drop-shadow-${contact.id})"/>
                <!-- Initials/count centered in the head area -->
                <text x="${pinWidth / 2}" y="26" text-anchor="middle" fill="white" font-family="Arial, Helvetica, sans-serif" font-size="${fontSize}" font-weight="bold" dominant-baseline="middle">
                    ${displayText}
                </text>
            </svg>
        `;

        return {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarker),
            size: new window.google.maps.Size(pinWidth, pinHeight),
            scaledSize: new window.google.maps.Size(pinWidth, pinHeight),
            anchor: new window.google.maps.Point(pinWidth / 2, pinHeight - 2)
        };
    };

    // Compute Google Map styles for dark/light themes
    const getMapStyles = (): any[] => ([
        // Hide Google branding and clean up UI
        {
            featureType: "all",
            elementType: "labels.text",
            stylers: [{ visibility: "off" }]
        },
        // Brighter, more vibrant map styling
        ...(computedTheme === 'dark' ? [
            { elementType: "geometry", stylers: [{ color: "#1a1a1a" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a1a" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
        ] : [
            { elementType: "geometry", stylers: [{ color: "#e5e7eb" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#e5e7eb" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#374151" }] },
        ]),
        {
            featureType: "administrative.locality",
            elementType: "labels.text.fill",
            stylers: [{ color: computedTheme === 'dark' ? "#f3f4f6" : "#111827" }],
        },
        {
            featureType: "poi",
            elementType: "labels.text.fill",
            stylers: [{ color: computedTheme === 'dark' ? "#f3f4f6" : "#111827" }],
        },
        {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [{ color: computedTheme === 'dark' ? "#0f172a" : "#cfe9bf" }],
        },
        {
            featureType: "poi.park",
            elementType: "labels.text.fill",
            stylers: [{ color: computedTheme === 'dark' ? "#6b7280" : "#4b5563" }],
        },
        {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: computedTheme === 'dark' ? "#374151" : "#c7cad1" }],
        },
        {
            featureType: "road",
            elementType: "geometry.stroke",
            stylers: [{ color: computedTheme === 'dark' ? "#1f2937" : "#a1a6b2" }],
        },
        {
            featureType: "road",
            elementType: "labels.text.fill",
            stylers: [{ color: computedTheme === 'dark' ? "#d1d5db" : "#374151" }],
        },
        {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [{ color: computedTheme === 'dark' ? "#4b5563" : "#b7bbc6" }],
        },
        {
            featureType: "road.highway",
            elementType: "geometry.stroke",
            stylers: [{ color: computedTheme === 'dark' ? "#374151" : "#a1a6b2" }],
        },
        {
            featureType: "road.highway",
            elementType: "labels.text.fill",
            stylers: [{ color: computedTheme === 'dark' ? "#f9fafb" : "#111827" }],
        },
        {
            featureType: "transit",
            elementType: "geometry",
            stylers: [{ color: computedTheme === 'dark' ? "#374151" : "#c7cad1" }],
        },
        {
            featureType: "transit.station",
            elementType: "labels.text.fill",
            stylers: [{ color: computedTheme === 'dark' ? "#f3f4f6" : "#111827" }],
        },
        {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: computedTheme === 'dark' ? "#0f172a" : "#bcd5f2" }],
        },
        {
            featureType: "water",
            elementType: "labels.text.fill",
            stylers: [{ color: computedTheme === 'dark' ? "#6b7280" : "#374151" }],
        },
        {
            featureType: "water",
            elementType: "labels.text.stroke",
            stylers: [{ color: computedTheme === 'dark' ? "#0f172a" : "#bcd5f2" }],
        },
    ]);

    const initializeMap = () => {
        if (!mapRef.current || !window.google) {
            console.log('DEBUG: Map initialization failed - mapRef:', !!mapRef.current, 'google:', !!window.google);
            return;
        }

        console.log('DEBUG: Starting map initialization');
        console.log('DEBUG: mapRef.current dimensions:', mapRef.current.offsetWidth, 'x', mapRef.current.offsetHeight);

        // Ensure InfoWindow chrome overrides are injected once (removes white bubble look/tail)
        try {
            if (!document.getElementById('gv-infowindow-override')) {
                const style = document.createElement('style');
                style.id = 'gv-infowindow-override';
                style.textContent = `
                    /* Remove default white bubble background and tail */
                    .gm-style .gm-style-iw-c { background: transparent !important; box-shadow: none !important; padding: 0 !important; }
                    .gm-style .gm-style-iw-d { overflow: visible !important; }
                    .gm-style .gm-style-iw-tc::after { display: none !important; }
                    .gm-style .gm-style-iw { background: transparent !important; }
                `;
                document.head.appendChild(style);
            }
        } catch { /* ignore */ }

        // Clear existing markers (support both classic and advanced markers)
        markersRef.current.forEach(markerData => {
            const m = markerData.marker;
            if (!m) return;
            if (typeof m.setMap === 'function') {
                m.setMap(null);
            } else if ('map' in m) {
                m.map = null;
            }
        });
        markersRef.current = [];

        // Get contacts with valid coordinates
        const contactsWithCoords = contacts.filter(contact => {
            const lat = parseFloat(String(contact.latitude));
            const lng = parseFloat(String(contact.longitude));
            return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
        });

        if (contactsWithCoords.length === 0) {
            console.log('DEBUG: No contacts with valid coordinates');
            return;
        }

        console.log('DEBUG: Creating map with', contactsWithCoords.length, 'contacts');

        // Choose the active Cloud Map ID for this initialization
        const activeCloudMapId = mapIdDark || mapId || null;

        // Create map with vertical bounds restriction
        const map = new window.google.maps.Map(mapRef.current, {
            zoom: 4,
            center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
            mapTypeId: (baseLayer === 'real' ? 'hybrid' : 'roadmap'),
            // Restrict vertical panning but allow horizontal movement
            restriction: {
                latLngBounds: {
                    north: 85,    // Prevent going too far north (white area)
                    south: -85,   // Prevent going too far south
                    west: -180,   // Allow full horizontal movement
                    east: 180,    // Allow full horizontal movement
                },
                strictBounds: true, // Enforce the bounds strictly
            },
            minZoom: 2,
            maxZoom: 18,
            gestureHandling: 'greedy',
            scrollwheel: true,
            disableDoubleClickZoom: false,
            draggable: true, // Enable map dragging
            keyboardShortcuts: false, // Disable keyboard shortcuts
            streetViewControl: false, // Hide street view person
            mapTypeControl: false, // Hide map/satellite toggle
            fullscreenControl: false, // Hide fullscreen control
            zoomControl: false, // Hide zoom controls
            rotateControl: false, // Hide rotate control
            scaleControl: false, // Hide scale control
            mapTypeControlOptions: {
                mapTypeIds: [] // Remove map type options
            },
            // Always use the dark or generic Map ID (enables Advanced Markers)
            ...(activeCloudMapId ? { mapId: activeCloudMapId } as any : {}),
            // Only if no Map ID exists at all, apply local dark styles as a fallback (faces may be limited)
            ...(!activeCloudMapId ? { styles: getMapStyles() } : {}),
        });

        mapInstanceRef.current = map;

        // Determine whether to allow Advanced Markers (only when a Map ID is actively set on the map)
        const allowAdvancedMarkers = Boolean(activeCloudMapId);

        // Restore saved center/zoom if available
        try {
            if (!restoredViewRef.current) {
                const raw = sessionStorage.getItem('mapView');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed && parsed.center && typeof parsed.zoom === 'number') {
                        map.setCenter(parsed.center);
                        map.setZoom(parsed.zoom);
                        restoredViewRef.current = true;
                    }
                }
            }
        } catch { }

        // Persist center/zoom on idle
        map.addListener('idle', () => {
            try {
                const c = map.getCenter();
                const z = map.getZoom();
                if (c && typeof z === 'number') {
                    sessionStorage.setItem('mapView', JSON.stringify({ center: { lat: c.lat(), lng: c.lng() }, zoom: z }));
                }
            } catch { }
        });

        // Add click listener to close any open info windows when clicking on the map
        map.addListener('click', () => {
            if (currentInfoWindowRef.current) {
                currentInfoWindowRef.current.close();
                currentInfoWindowRef.current = null;
            }
        });

        // Group contacts by location
        const locationGroups = new Map<string, ContactWithLocation[]>();
        contactsWithCoords.forEach(contact => {
            const key = `${contact.latitude},${contact.longitude}`;
            if (!locationGroups.has(key)) {
                locationGroups.set(key, []);
            }
            locationGroups.get(key)!.push(contact);
        });

        // Create markers with profile pictures
        const bounds = new window.google.maps.LatLngBounds();

        locationGroups.forEach((groupContacts, key) => {
            const contact = groupContacts[0];

            // Validate latitude and longitude
            const lat = parseFloat(String(contact.latitude));
            const lng = parseFloat(String(contact.longitude));

            if (isNaN(lat) || isNaN(lng)) {
                console.warn(`Invalid coordinates for contact ${contact.id}: lat=${contact.latitude}, lng=${contact.longitude}`);
                return; // Skip this contact
            }

            const position = { lat, lng };
            const count = groupContacts.length;

            // Create marker (prefer HTML AdvancedMarkerElement)
            let marker: any;
            const maybeAdvanced = createCustomMarker(contact, count, allowAdvancedMarkers);
            const AdvancedMarkerClass = (window as any).google?.maps?.marker?.AdvancedMarkerElement;
            if (allowAdvancedMarkers && AdvancedMarkerClass && (maybeAdvanced instanceof AdvancedMarkerClass)) {
                maybeAdvanced.position = position;
                maybeAdvanced.map = map;
                marker = maybeAdvanced;
            } else {
                const markerIcon = maybeAdvanced; // SVG fallback
                marker = new window.google.maps.Marker({
                    position,
                    map,
                    icon: markerIcon,
                    title: count > 1 ? `${count} contacts` : `${contact.first_name} ${contact.last_name}`,
                    optimized: false // Important for SVG markers
                });
            }
            // Create enhanced info window content with better layout, no tier badge, and clickable/copyable fields
            const iwColors = (computedTheme === 'dark')
                ? {
                    containerBg: 'rgba(3,7,18,0.88)', // near-black glass
                    border: 'rgba(59,130,246,0.25)',   // subtle blue border
                    text: '#e5e7eb',                   // gray-200
                    subtext: '#a5b4fc',                // indigo-300
                    chipBg: 'rgba(31,41,55,0.85)',     // gray-800 glass
                    chipHover: 'rgba(55,65,81,0.9)',   // gray-700 glass
                    accent: '#60a5fa',                 // blue-400
                    avatarFallbackBg: '#4b5563',       // gray-600
                    avatarBorder: 'rgba(255,255,255,0.7)',
                    shadow: '0 10px 30px rgba(2,6,23,0.7)'
                }
                : {
                    containerBg: 'rgba(255,255,255,0.95)',
                    border: 'rgba(37,99,235,0.35)',
                    text: '#111827',
                    subtext: '#4b5563',
                    chipBg: 'rgba(243,244,246,0.95)',
                    chipHover: 'rgba(229,231,235,0.95)',
                    accent: '#2563eb',
                    avatarFallbackBg: '#9ca3af',
                    avatarBorder: '#374151',
                    shadow: '0 10px 30px rgba(0,0,0,0.25)'
                };

            const infoWindowContent = `
                <div style="background: ${iwColors.containerBg}; color: ${iwColors.text}; padding: 16px; border-radius: 0; max-width: 360px; width: 340px; font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; border: 1px solid ${iwColors.border}; user-select: text; -webkit-user-select: text; box-shadow: ${iwColors.shadow}; backdrop-filter: blur(6px);">
                    <div style="display: flex; align-items: center; margin-bottom: 12px; gap: 8px;">
                        <svg width="16" height="16" fill="${iwColors.accent}">
                            <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 14c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z"/>
                        </svg>
                        <span style="font-weight: 600; color: ${iwColors.subtext};">${contact.locationString}</span>
                    </div>
                    ${groupContacts.slice(0, 3).map(c => {
                const safePhoneHref = c.phone ? `tel:${(c.phone || '').replace(/[^+\d]/g, '')}` : '';
                const safeEmailHref = c.email ? `mailto:${(c.email || '').trim()}` : '';
                return `
                        <div style="background: ${iwColors.chipBg}; padding: 12px; border-radius: 10px; margin-bottom: 8px; cursor: pointer; transition: background-color 0.2s, transform 0.1s;"
                             onclick="window.selectContact('${c.id}')"
                             onmouseover="this.style.backgroundColor='${iwColors.chipHover}'; this.style.transform='translateY(-1px)';"
                             onmouseout="this.style.backgroundColor='${iwColors.chipBg}'; this.style.transform='translateY(0)';">
                            <div style="display: flex; align-items: center; gap: 10px;">
                                ${c.profile_image_url ? `
                                    <img src="${c.profile_image_url.startsWith('http') ? c.profile_image_url : `https://${c.profile_image_url}`}" 
                                 style="width: 40px; height: 40px; border-radius: 0; object-fit: cover; border: 2px solid ${iwColors.avatarBorder}; flex-shrink: 0; box-shadow: 0 2px 6px rgba(0,0,0,0.25);" 
                                         alt="${c.first_name} ${c.last_name}"
                                         onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                             <div style="display:none; width: 40px; height: 40px; border-radius: 0; background: ${iwColors.avatarFallbackBg}; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; color: white; border: 2px solid ${iwColors.avatarBorder}; flex-shrink: 0;">
                                        ${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}
                                    </div>
                                ` : `
                                    <div style="width: 40px; height: 40px; border-radius: 50%; background: ${iwColors.avatarFallbackBg}; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; color: white; border: 2px solid ${iwColors.avatarBorder}; flex-shrink: 0;">
                                        ${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}
                                    </div>
                                `}
                                <div style="flex: 1; min-width: 0;">
                                    <div style="display:flex; align-items:center; gap:8px; margin-bottom: 2px;">
                                        <span style="font-weight: 800; color: ${iwColors.text};">${c.first_name} ${c.last_name}</span>
                                    </div>
                                    ${c.title || c.company ? `
                                        <div style="color: ${iwColors.subtext}; font-size: 12px; margin-bottom: 6px; overflow-wrap:anywhere;">
                                            ${c.title || ''}${c.title && c.company ? ' at ' : ''}${c.company || ''}
                                        </div>
                                    ` : ''}
                                    <div style="display: flex; flex-wrap: wrap; gap: 10px; font-size: 12px; color: ${iwColors.subtext};">
                                        ${c.email ? `<a href="${safeEmailHref}" onclick="event.stopPropagation();" style="display: inline-flex; align-items: center; gap: 4px; color: ${iwColors.subtext}; text-decoration: none; overflow-wrap:anywhere;">
                                            <span>📧</span><span>${c.email}</span>
                                        </a>` : ''}
                                        ${c.phone ? `<a href="${safePhoneHref}" onclick="event.stopPropagation();" style="display: inline-flex; align-items: center; gap: 4px; color: ${iwColors.subtext}; text-decoration: none; overflow-wrap:anywhere;">
                                            <span>📞</span><span>${c.phone}</span>
                                        </a>` : ''}
                                    </div>
                                </div>
                            </div>
                        </div>
                    `;
            }).join('')}
                    ${count > 3 ? `
                        <div style="text-align: center; color: ${iwColors.subtext}; font-size: 12px; padding: 8px;">
                            <button id="gv-more-toggle" style="background: transparent; border: none; color: ${iwColors.accent}; font-weight: 700; cursor: pointer;">+${count - 3} more contacts at this location</button>
                        </div>
                        <div id="gv-more-list" style="display:none; max-height: 220px; overflow:auto; padding-top: 4px; margin-top: 4px; border-top: 1px dashed ${iwColors.border}">
                            ${groupContacts.slice(3).map(c => {
                const safePhoneHref = c.phone ? `tel:${(c.phone || '').replace(/[^+\\d]/g, '')}` : '';
                const safeEmailHref = c.email ? `mailto:${(c.email || '').trim()}` : '';
                return `
                                <div style="background: ${iwColors.chipBg}; padding: 10px; border-radius: 10px; margin-bottom: 8px; cursor: pointer;"
                                     onclick="window.selectContact('${c.id}')">
                                    <div style="display:flex; align-items:center; gap:10px;">
                                        ${c.profile_image_url ? `
                                            <img src="${c.profile_image_url.startsWith('http') ? c.profile_image_url : `https://${c.profile_image_url}`}" style="width:34px;height:34px;border-radius:50%;object-fit:cover;border:1px solid ${iwColors.avatarBorder};" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"/>
                                            <div style="display:none;width:34px;height:34px;border-radius:50%;background:${iwColors.avatarFallbackBg};align-items:center;justify-content:center;color:white;font-weight:700;border:1px solid ${iwColors.avatarBorder};">${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}</div>
                                        ` : `
                                            <div style="width:34px;height:34px;border-radius:50%;background:${iwColors.avatarFallbackBg};display:flex;align-items:center;justify-content:center;color:white;font-weight:700;border:1px solid ${iwColors.avatarBorder};">${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}</div>
                                        `}
                                        <div style="flex:1;min-width:0;">
                                            <div style="font-weight:700;color:${iwColors.text};">${c.first_name} ${c.last_name}</div>
                                            <div style="display:flex;flex-wrap:wrap;gap:10px;font-size:12px;color:${iwColors.subtext};">
                                                ${c.email ? `<a href="${safeEmailHref}" onclick="event.stopPropagation();" style="color:${iwColors.subtext};text-decoration:none;overflow-wrap:anywhere;">📧 ${c.email}</a>` : ''}
                                                ${c.phone ? `<a href="${safePhoneHref}" onclick="event.stopPropagation();" style="color:${iwColors.subtext};text-decoration:none;overflow-wrap:anywhere;">📞 ${c.phone}</a>` : ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>`;
            }).join('')}
                        </div>
                    ` : ''}
                </div>
            `;

            const infoWindow = new window.google.maps.InfoWindow({
                content: infoWindowContent,
                headerDisabled: true,
                pixelOffset: new window.google.maps.Size(0, -10)
            });

            // Ensure interactive elements inside the InfoWindow are wired up when the DOM is ready
            infoWindow.addListener('domready', () => {
                try {
                    const toggle = document.getElementById('gv-more-toggle');
                    const list = document.getElementById('gv-more-list');
                    if (toggle && list) {
                        toggle.addEventListener('click', (e) => {
                            e.stopPropagation();
                            const cur = list.style.display;
                            list.style.display = (!cur || cur === 'none') ? 'block' : 'none';
                        }, { once: false });
                    }
                } catch { /* ignore */ }
            });

            const openInfo = () => {
                // Close any currently open info window
                if (currentInfoWindowRef.current) {
                    currentInfoWindowRef.current.close();
                }

                // Open the new info window and store reference
                infoWindow.open(map, marker);
                currentInfoWindowRef.current = infoWindow;
            };
            // Support both classic and advanced marker click events
            try { marker.addListener('click', openInfo); } catch { }
            try { marker.addListener('gmp-click', openInfo); } catch { }

            // Clear reference when info window is closed
            infoWindow.addListener('closeclick', () => {
                currentInfoWindowRef.current = null;
            });

            markersRef.current.push({ marker, contact, groupContacts });
            bounds.extend(position);
        });

        // Fit map to markers
        if (!restoredViewRef.current && contactsWithCoords.length > 1) {
            map.fitBounds(bounds);
        } else if (!restoredViewRef.current && contactsWithCoords.length === 1) {
            const centerLat = parseFloat(String(contactsWithCoords[0].latitude));
            const centerLng = parseFloat(String(contactsWithCoords[0].longitude));

            if (!isNaN(centerLat) && !isNaN(centerLng)) {
                map.setCenter({ lat: centerLat, lng: centerLng });
                map.setZoom(10);
            }
        }

        // Global function for info window clicks
        window.selectContact = (contactId: string) => {
            const contact = contacts.find(c => c.id === contactId);
            if (contact) {
                onContactSelect(contact);
            }
        };

        console.log('DEBUG: Map initialization completed successfully');
    };

    // Theme switching disabled: keep dark Map ID or generic Map ID active at all times
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !(window as any).google?.maps) return;
        try {
            // Respect selected base layer (roadmap vs hybrid)
            try { map.setMapTypeId(baseLayer === 'real' ? 'hybrid' : 'roadmap'); } catch { }
            const active = mapIdDark || mapId || null;
            if (active) {
                map.setOptions({ mapId: active, styles: null });
            } else {
                map.setOptions({ styles: getMapStyles(), mapId: null });
            }
        } catch (e) {
            console.warn('Failed to enforce dark map style', e);
        }
    }, [mapIdDark, mapId, baseLayer]);

    // Persist base layer selection
    useEffect(() => {
        try { localStorage.setItem('mapBaseLayer', baseLayer); } catch { }
        const map = mapInstanceRef.current;
        if (map && (window as any).google?.maps) {
            try { map.setMapTypeId(baseLayer === 'real' ? 'hybrid' : 'roadmap'); } catch { }
        }
    }, [baseLayer]);

    // Inline overlay helper (keeps app header/other UI visible)
    const renderOverlay = () => {
        if (error) {
            return (
                <div className={`absolute inset-0 ${theme.overlayBg} backdrop-blur-sm flex items-center justify-center z-[1200]`}>
                    <div className="text-center">
                        <MapPin className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                        <p className="text-lg" style={{ color: theme.overlayText }}>{error}</p>
                    </div>
                </div>
            );
        }
        if (loading || mapLoading) {
            return (
                <div className={`absolute inset-0 ${theme.overlayBg} backdrop-blur-sm flex items-center justify-center z-[1200]`}>
                    <div className="text-center">
                        <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-4 animate-spin" />
                        {loading && geocodingProgress.total > 0 ? (
                            <div>
                                <p style={{ color: theme.overlayText }}>
                                    {geocodingProgress.current === 0 ?
                                        'Loading saved coordinates...' :
                                        `Geocoding new contacts... (${geocodingProgress.current}/${geocodingProgress.total})`}
                                </p>
                                <p className="text-sm" style={{ color: theme.overlaySub }}>
                                    {geocodingProgress.current === 0 ?
                                        'Checking for previously saved locations' :
                                        'Saving coordinates for future use'}
                                </p>
                                <div className="w-64 bg-gray-300 dark:bg-gray-700 rounded-full h-2 mx-auto mt-2">
                                    <div
                                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${(geocodingProgress.current / Math.max(1, geocodingProgress.total)) * 100}%` }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <p style={{ color: theme.overlayText }}>Loading map…</p>
                        )}
                    </div>
                </div>
            );
        }
        if (contacts.length === 0) {
            return (
                <div className={`absolute inset-0 ${theme.overlayBg} backdrop-blur-sm flex items-center justify-center z-[1200]`}>
                    <div className="text-center">
                        <MapPin className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                        <p className="text-lg" style={{ color: theme.overlayText }}>No contacts with locations found</p>
                        <p className="text-sm mt-2" style={{ color: theme.overlaySub }}>Add location info to see pins on the map</p>
                    </div>
                </div>
            );
        }
        return null;
    };

    const contactsWithCoords = contacts.filter(c => {
        const lat = parseFloat(String(c.latitude));
        const lng = parseFloat(String(c.longitude));
        return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    });

    // Count no longer uses tiers; relying on filteredContacts visible count

    return (
        <div className={`relative h-full ${theme.appBg} ${theme.appText} overflow-hidden flex flex-col`}>
            {/* Google Map - Takes available space */}
            <div ref={mapRef} className="flex-1 min-h-[360px]" />
            {/* Overlay (loading/error/empty) */}
            {renderOverlay()}
            {/* Bottom search + one filter dropdown (no tier filters) */}
            <div className={`z-[1000] ${theme.barBg} border-t ${theme.barBorder} p-4 space-y-3`}>
                <div className="relative flex items-center gap-2">
                    {/* Keep the input and its icons in a fixed-height relative wrapper so opening the dropdown below doesn't shift them */}
                    <div className="flex-1">
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                                <Search className="w-5 h-5" />
                            </div>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search"
                                className={`w-full pl-12 pr-32 py-3 ${theme.inputBg} border ${theme.inputBorder} ${theme.inputText} ${theme.inputPlaceholder} focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors rounded-lg`}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 z-30">
                                {/* Base layer toggle: Road vs Real */}
                                <button
                                    type="button"
                                    onClick={() => setBaseLayer(prev => prev === 'road' ? 'real' : 'road')}
                                    className="h-9 px-3 rounded-md text-gray-200 border border-gray-700 hover:bg-gray-800/60 focus:outline-none focus:ring-2 focus:ring-gray-600 text-sm"
                                    title={`Base map: ${baseLayer === 'road' ? 'Road' : 'Real'}`}
                                    aria-label="Toggle base map"
                                >
                                    {baseLayer === 'road' ? 'Road' : 'Real'}
                                </button>
                                <button
                                    ref={filterButtonRef}
                                    type="button"
                                    onClick={() => setShowFieldDropdown(v => !v)}
                                    className="w-9 h-9 grid place-items-center rounded-md text-gray-300 hover:text-white hover:bg-gray-800/60 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    title="Toggle filters"
                                    aria-label="Toggle filters"
                                >
                                    <Filter className="w-5 h-5" />
                                </button>
                                {searchQuery && (
                                    <button
                                        type="button"
                                        aria-label="Clear search"
                                        title="Clear"
                                        onClick={() => setSearchQuery('')}
                                        className={`w-7 h-7 grid place-items-center rounded ${theme.panelBg} border ${theme.panelBorder} ${theme.appText} hover:bg-gray-100/60`}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>
                        {/* Inline filter panel sits below the input, without affecting icon positioning */}
                        {showFieldDropdown && (
                            <div ref={filterDropdownRef} className={`mt-2 w-full ${theme.panelBg} border ${theme.panelBorder} rounded-lg shadow-lg relative z-20 p-3`}>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {allFieldFilters.map(ff => {
                                        const checked = selectedFieldFilters.includes(ff);
                                        const label = ff === 'name' ? 'Name' : ff === 'company' ? 'Company' : ff === 'location' ? 'Location' : ff === 'collection' ? 'Collection' : ff === 'status' ? 'Status' : ff === 'relationship' ? 'Relationship' : 'Updated';
                                        return (
                                            <button
                                                key={ff}
                                                type="button"
                                                className={
                                                    `w-full px-3 py-2 rounded-md text-sm border transition-colors ` +
                                                    (checked
                                                        ? 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 text-white border-gray-500 hover:from-gray-800 hover:to-gray-600'
                                                        : `${theme.chipBg} ${theme.chipText} ${theme.panelBorder} hover:bg-gray-200`)
                                                }
                                                onClick={() => {
                                                    setSelectedFieldFilters(prev => {
                                                        if (prev.includes(ff)) return prev.filter(x => x !== ff);
                                                        return [...prev, ff];
                                                    });
                                                }}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="flex justify-between items-center pt-2 mt-3 border-t border-gray-700">
                                    <button
                                        type="button"
                                        className={`text-xs px-2 py-1 rounded border ${theme.panelBorder} ${theme.appText} hover:bg-gray-100/60`}
                                        onClick={() => setSelectedFieldFilters(allFieldFilters)}
                                    >
                                        Select all
                                    </button>
                                    <button
                                        type="button"
                                        className={`text-xs px-2 py-1 rounded border ${theme.panelBorder} ${theme.appText} hover:bg-gray-100/60`}
                                        onClick={() => setSelectedFieldFilters([])}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {searchQuery.trim() ? (
                    <>
                        <div className={`${theme.panelBg} border ${theme.panelBorder} rounded-lg max-h-64 overflow-y-auto`}>
                            {filteredContacts.length === 0 ? (
                                <div className={`p-4 text-sm ${theme.appText} opacity-70`}>No matches</div>
                            ) : (
                                filteredContacts.slice(0, 50).map(c => (
                                    <button
                                        key={c.id}
                                        onClick={() => focusContactOnMap(c.id!)}
                                        className={`w-full flex items-center gap-3 p-3 border-b ${theme.panelBorder} last:border-b-0 hover:bg-gray-100/60 text-left`}
                                    >
                                        <div className="w-9 h-9 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
                                            {c.profile_image_url ? (
                                                <img src={c.profile_image_url.startsWith('http') ? c.profile_image_url : `https://${c.profile_image_url}`} alt={`${c.first_name} ${c.last_name}`} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs font-semibold">
                                                    {(c.first_name?.[0] || '')}{(c.last_name?.[0] || '')}
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className={`font-medium truncate ${theme.appText}`}>{c.first_name} {c.last_name}</div>
                                            <div className="text-xs text-gray-500 truncate">{c.locationString || [c.city, c.state].filter(Boolean).join(', ')}</div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                        <div className="flex items-center justify-end text-gray-500 text-sm">
                            {filteredContacts.length} contacts shown
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}
